import React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import type { ConnectorStatus } from '@/types/connectors';
import { getPlatformMeta } from './platformLogos';

const STATUS_OPTIONS: ConnectorStatus[] = ['connected', 'running', 'error', 'disabled'];

export interface ConnectorFiltersProps {
  platforms: string[];
  statuses: ConnectorStatus[];
  onTogglePlatform: (platform: string) => void;
  onToggleStatus: (status: ConnectorStatus) => void;
  onClearAll: () => void;
  platformCounts: Record<string, number>;
  statusCounts: Partial<Record<ConnectorStatus, number>>;
}

export const ConnectorFilters: React.FC<ConnectorFiltersProps> = ({
  platforms,
  statuses,
  onTogglePlatform,
  onToggleStatus,
  onClearAll,
  platformCounts,
  statusCounts,
}) => {
  const allPlatforms = Object.keys(platformCounts).sort((a, b) =>
    getPlatformMeta(a).displayName.localeCompare(getPlatformMeta(b).displayName)
  );
  const hasActive = platforms.length > 0 || statuses.length > 0;

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            Platform {platforms.length > 0 && `(${platforms.length})`}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-2 w-72 max-h-80 overflow-auto">
          {allPlatforms.map((p) => {
            const id = `flt-platform-${p}`;
            const checked = platforms.includes(p);
            return (
              <label
                key={p}
                htmlFor={id}
                className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded cursor-pointer"
                aria-label={`${p} (${platformCounts[p] ?? 0})`}
              >
                <Checkbox id={id} checked={checked} onCheckedChange={() => onTogglePlatform(p)} />
                <span className="flex-1">{getPlatformMeta(p).displayName}</span>
                <span className="text-xs text-muted-foreground">{platformCounts[p] ?? 0}</span>
              </label>
            );
          })}
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            Status {statuses.length > 0 && `(${statuses.length})`}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-2 w-56">
          {STATUS_OPTIONS.map((s) => {
            const id = `flt-status-${s}`;
            const checked = statuses.includes(s);
            return (
              <label
                key={s}
                htmlFor={id}
                className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded cursor-pointer"
              >
                <Checkbox id={id} checked={checked} onCheckedChange={() => onToggleStatus(s)} />
                <span className="flex-1 capitalize">{s}</span>
                <span className="text-xs text-muted-foreground">{statusCounts[s] ?? 0}</span>
              </label>
            );
          })}
        </PopoverContent>
      </Popover>

      {hasActive && (
        <Button variant="ghost" size="sm" onClick={onClearAll}>
          <X className="h-3 w-3 mr-1" />
          Clear all
        </Button>
      )}
    </div>
  );
};
